import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { pmService, type PayerPriority } from "../../services/pm.service";
import { Select } from "../ui/Select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// System Payer Denial (acknowledgement-based, includes claims denied by both
// payer and clearinghouse) vs Real Payer Denial (inferred from $0 insurance
// remittances in Payments Expanded — Payment Method='Non-Payment', Payment
// Amount=0, Payment Payer Type='Insurance'). These are largely disjoint claim
// populations, not a superset/subset — rendered as grouped, not stacked, bars.
const SYSTEM_COLOR = "#699CBB";
const REAL_COLOR = "#e05252";
const THRESHOLD = 5;
const CURRENT_YEAR = new Date().getFullYear();

const formatMoney = (value: number, compact = false): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value);

const FIRST_YEAR = 2015;

const PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: "ytd", label: "Year to Date" },
  ...Array.from({ length: CURRENT_YEAR - FIRST_YEAR + 1 }, (_, i) => {
    const y = CURRENT_YEAR - i;
    return { value: String(y), label: String(y) };
  }),
];

interface FilterState {
  period: string;
  priority: PayerPriority;
}

const defaultFilter = (): FilterState => ({ period: "ytd", priority: "primary" });

export const PayerDenialComparisonWidget: React.FC = () => {
  const [pending, setPending] = useState<FilterState>(defaultFilter);
  const [applied, setApplied] = useState<FilterState>(defaultFilter);

  const hasChanges = pending.period !== applied.period || pending.priority !== applied.priority;

  const { data, isLoading } = useQuery({
    queryKey: ["pm-payer-denial-comparison", applied.period, applied.priority],
    queryFn: () => {
      const ytd = applied.period === "ytd";
      const year = ytd ? CURRENT_YEAR : parseInt(applied.period, 10);
      return pmService.getPayerDenialComparison(year, ytd, applied.priority);
    },
  });

  const months = data?.months ?? [];
  const categories = months.map((m) => MONTHS[m.month - 1]);
  const systemRates = months.map((m) => m.systemPayerDenialRate);
  const realRates = months.map((m) => m.realPayerDenialRate);
  const maxRate = months.length > 0 ? Math.max(...systemRates, ...realRates) : 0;
  const yAxisMax = Math.ceil(Math.max(maxRate * 1.25, THRESHOLD + 2));

  const totalClaims = months.reduce((s, m) => s + m.totalClaims, 0);
  const totalSystemDeniedClaims = months.reduce((s, m) => s + m.systemPayerDeniedClaims, 0);
  const totalRealDeniedClaims = months.reduce((s, m) => s + m.realPayerDeniedClaims, 0);
  const systemRate = totalClaims > 0 ? (totalSystemDeniedClaims / totalClaims) * 100 : 0;
  const realRate = totalClaims > 0 ? (totalRealDeniedClaims / totalClaims) * 100 : 0;

  const totalCharge = months.reduce((s, m) => s + m.totalChargeAmount, 0);
  const totalSystemDeniedAmount = months.reduce((s, m) => s + m.systemPayerDeniedAmount, 0);
  const totalRealDeniedAmount = months.reduce((s, m) => s + m.realPayerDeniedAmount, 0);

  const chartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: [SYSTEM_COLOR, REAL_COLOR],
    plotOptions: {
      bar: {
        borderRadius: 2,
        borderRadiusApplication: "end",
        columnWidth: "65%",
      },
    },
    dataLabels: { enabled: false },
    annotations: {
      yaxis: [
        {
          y: THRESHOLD,
          borderColor: "#e05252",
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            text: `${THRESHOLD}% threshold`,
            position: "right",
            borderColor: "transparent",
            style: {
              background: "transparent",
              color: "#e05252",
              fontSize: "11px",
              fontWeight: "600",
            },
          },
        },
      ],
    },
    xaxis: {
      categories,
      labels: { style: { fontSize: "12px", colors: "#6b7280" } },
      axisBorder: { show: true, color: "#BCBDC5" },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: yAxisMax,
      labels: {
        formatter: (val: number) => `${val}%`,
        style: { fontSize: "11px", colors: "#6b7280" },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "12px",
      markers: { size: 6 },
      itemMargin: { horizontal: 12 },
    },
    grid: {
      strokeDashArray: 3,
      borderColor: "#e5e7eb",
      padding: { right: 80 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const m = months[dataPointIndex];
        if (!m) return "";
        const rows = [
          {
            color: SYSTEM_COLOR,
            label: "System Payer Denial",
            count: `${m.systemPayerDeniedClaims.toLocaleString()} claims`,
            value: `${m.systemPayerDenialRate}%`,
            amount: formatMoney(m.systemPayerDeniedAmount),
          },
          {
            color: REAL_COLOR,
            label: "Solentis Real Payer Denial",
            count: `${m.realPayerDeniedClaims.toLocaleString()} claims`,
            value: `${m.realPayerDenialRate}%`,
            amount: formatMoney(m.realPayerDeniedAmount),
          },
        ];
        const rowsHtml = rows
          .map(
            (r) => `
              <div style="display:flex;align-items:center;gap:6px;padding:2px 0;">
                <span style="width:10px;height:2px;background:${r.color};display:inline-block;flex-shrink:0;"></span>
                <span style="color:#52514e;font-size:11px;flex:1;">${r.label}</span>
                <span style="color:#0b0b0b;font-weight:600;font-size:12px;margin-left:8px;">${r.count} (${r.value})</span>
                <span style="color:#898781;font-size:11px;margin-left:4px;">${r.amount}</span>
              </div>`
          )
          .join("");
        return `
          <div style="padding:8px 10px;min-width:300px;">
            <div style="font-weight:700;font-size:12px;color:#0b0b0b;">${categories[dataPointIndex]} — ${m.totalClaims.toLocaleString()} claims total</div>
            <div style="color:#898781;font-size:11px;margin-bottom:4px;">${formatMoney(m.totalChargeAmount)} charged</div>
            ${rowsHtml}
          </div>`;
      },
    },
  };

  return (
    <div className="bg-white border-2 border-border-gray">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Payer Denials: System vs Real</h3>
        <span className="text-xs sm:text-sm">
          What Nextech reports vs Solentis' estimate from $0 insurance remittances
        </span>
      </div>

      {/* Controls */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
          <Select
            value={pending.period}
            onChange={(e) => setPending((prev) => ({ ...prev, period: e.target.value }))}
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Payor</label>
          <Select
            value={pending.priority}
            onChange={(e) =>
              setPending((prev) => ({ ...prev, priority: e.target.value as PayerPriority }))
            }
          >
            <option value="all">All</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="primary_secondary">Primary+Secondary</option>
            <option value="tertiary">Tertiary</option>
          </Select>
        </div>

        <button
          type="button"
          onClick={() => setApplied(pending)}
          disabled={!hasChanges}
          className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
        >
          Apply
        </button>
      </div>

      {/* Chart */}
      <div className="px-4 pb-4 pt-2">
        {isLoading && <div className="text-center py-12 text-gray-500">Loading KPI data...</div>}

        {!isLoading && months.length === 0 && (
          <div className="text-center py-12 text-gray-500">No data for the selected period.</div>
        )}

        {!isLoading && months.length > 0 && (
          <>
            {/* Stat tiles — claim counts lead (drive the chart); $ amounts follow
                as muted secondary context. No "hidden gap" tile here — System and
                Real are independent lenses, not superset/subset like the System
                Denials widget. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 pt-2">
              <StatTile
                label="Total claims"
                value={totalClaims.toLocaleString()}
                sub={`${formatMoney(totalCharge, true)} charged`}
              />
              <StatTile
                label="System Payer denied"
                value={`${totalSystemDeniedClaims.toLocaleString()} claims`}
                sub={`${systemRate.toFixed(1)}% · ${formatMoney(totalSystemDeniedAmount, true)}`}
                swatchColor={SYSTEM_COLOR}
              />
              <StatTile
                label="Real Payer denied (est.)"
                value={`${totalRealDeniedClaims.toLocaleString()} claims`}
                sub={`${realRate.toFixed(1)}% · ${formatMoney(totalRealDeniedAmount, true)}`}
                swatchColor={REAL_COLOR}
                emphasize
              />
            </div>

            <ReactApexChart
              // Force a clean remount when the filters change — categories/series
              // length can change between periods (e.g. YTD's partial month count
              // vs a full year), and ApexCharts' internal tooltip index arrays can
              // crash ("Cannot read properties of undefined (reading 'x')") if it
              // tries to reconcile in place instead of reinitializing.
              key={`${applied.period}-${applied.priority}`}
              options={chartOptions}
              series={[
                { name: "System Payer Denial Rate", data: systemRates },
                { name: "Solentis Real Payer Denial Rate", data: realRates },
              ]}
              type="bar"
              height={320}
            />
          </>
        )}
      </div>
    </div>
  );
};

interface StatTileProps {
  label: string;
  value: string;
  sub?: string;
  swatchColor?: string;
  emphasize?: boolean;
}

// Value stays in ink tokens (never the series color) — identity comes from the
// small swatch dot beside the label, not from coloring the number itself.
const StatTile: React.FC<StatTileProps> = ({ label, value, sub, swatchColor, emphasize }) => (
  <div
    className={`border p-3 ${emphasize ? "bg-red-50 border-red-200" : "bg-gray-50 border-border-gray"}`}
  >
    <div className="flex items-center gap-1.5 text-[11px] text-gray-500 uppercase tracking-wide font-bold mb-1">
      {swatchColor && (
        <span
          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: swatchColor }}
        />
      )}
      {label}
    </div>
    <div
      className={`font-bold text-lg leading-tight ${emphasize ? "text-red-600" : "text-gray-800"}`}
    >
      {value}
    </div>
    {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
  </div>
);
